import React, {
  ComponentType,
  useState
} from 'react'
import {
  Button, Divider,
  Form,
  Header,
  Message
} from 'semantic-ui-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
// Local
import { LoginLogo } from '../common/brandingUtils'
import { history } from '../../store'
import {
  PAGES,
  shouldRenderReskin2025
} from '../../constant'
import If from '../common/if'
import ClinicalIntelligenceLogo from '../logos/ClinicalIntelligenceLogo'
import ReveleerLogoStacked from '../logos/ReveleerLogoStacked'
import ReveleerButton from '../common/ReveleerButton'
import ReveleerCoachingBox from '../ReveleerCoachingBox'
import './LoginForm.css'
import { BASE_URL } from "../../config";


const SafeForm = Form as unknown as ComponentType<any>
const SafeHeader = Header as unknown as ComponentType<any>

/**
 * Provides a login form
 * @param id An ID attribute prefix used to identify component elements
 * @param onSubmit Login form submit callback function
 *        Arguments: username and password
 * @param username Optional default username
 * @param errorMessage Optional default error message
 */
type Props = {
  errorMessage: string | undefined
  id: string
  onSubmit: (
    username: string,
    password: string,
    token: string | null
  ) => void
  username: string | undefined
}

function LoginForm(
  props: Props
): JSX.Element {
  const {
    errorMessage: errorMessageProp,
    id: idProp,
    onSubmit: onSubmitProp,
    username: usernameProp
  } = props

  const { executeRecaptcha } = useGoogleReCaptcha()

  const [
    usernameState,
    setUsernameState
  ] = useState<string>(
    usernameProp || ''
  )
  const [
    passwordState,
    setPasswordState
  ] = useState<string>('')
  const [
    isSubmittingState,
    setIsSubmittingState
  ] = useState<boolean>(false)

  const onSubmit = async () => {
    if (!usernameState || !passwordState || isSubmittingState) return
    setIsSubmittingState(true)

    if (executeRecaptcha) {
      const token = await executeRecaptcha("login")
      if (token) {
        onSubmitProp(usernameState, passwordState, token)
      }
      
      setIsSubmittingState(false)
    }
  }

  const onChange = (
    e: any,
    value: {
      name: null
      value: null
    } | null
  ) => {
    if (
      value !== null &&
      value.name !== null &&
      value.value !== null
    ) {
      if (value.name === 'username') {
        setUsernameState(value.value)
        return
      }
      if (value.name === 'password') {
        setPasswordState(value.value)
      }
    }
  }

  const onForgotPassword = () => {
    history.push(PAGES.FORGOT_PASSWORD)
  }

  return (
    <>
      <SafeForm error={ errorMessageProp != null }>
        <SafeHeader
          as="h2"
          textAlign="center"
        >
          <Header.Content>
            <If isTrue={ !shouldRenderReskin2025 }>
              <LoginLogo />
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerLogoStacked />
              <ClinicalIntelligenceLogo />
            </If>
          </Header.Content>
        </SafeHeader>
        <If isTrue={ !!errorMessageProp }>
          <If isTrue={ !shouldRenderReskin2025 }>
            <Message
              error={ true }
              content={ errorMessageProp }
            />
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerCoachingBox
              coachingBoxType="error"
              isShowing
              message={
                <p>
                  { errorMessageProp }
                </p>
              }
            />
          </If>
        </If>
        <Form.Input
          id={ `${idProp}-login-username` }
          label={
            shouldRenderReskin2025
              ? ''
              : 'Username:'
          }
          placeholder="Username"
          onChange={ onChange }
          name="username"
          value={ usernameState }
        />
        <Form.Input
          id={ `${idProp}-login-password` }
          label={
            shouldRenderReskin2025
              ? ''
              : 'Password:'
          }
          placeholder="Password"
          onChange={ onChange }
          name="password"
          value={ passwordState }
          type="password"
        />
        <div
          className="right-text linkable"
          id="forgotpassword"
          onClick={ onForgotPassword }>
          Forgot Password?
        </div>
        <If isTrue={ !shouldRenderReskin2025 }>
          <div className='login-button-wrapper'>
            <Button
              onClick={ onSubmit }
              disabled={ !usernameState.length || !passwordState.length }
              id={ `${idProp}-login-submit` }
              color="blue"
              type="submit"
            >
              Submit
            </Button>
          </div>
        </If>
        <If isTrue={ shouldRenderReskin2025 }>
          <ReveleerButton
            buttonLabel="Submit"
            buttonSize="large"
            buttonType="primary"
            id=""
            isDisabled={ !usernameState.length || !passwordState.length }
            onClick={ onSubmit }
            type="submit"
          />
        </If>
      </SafeForm>
      <Divider horizontal className='login-form-divider'>Or</Divider>
      <div className='sso-form'>
        <a className='ui icon left labeled button sso-button' href={`${BASE_URL}/api/oauth/auth0/launch`}>
          <i className='icon'>
            <svg width="100%" height="100%" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_2824_47)">
                <circle cx="30" cy="30" r="30" fill="#E5573F"/>
                <path d="M49.9494 20.5144C48.4945 19.07 45.8778 19.0596 44.3184 20.6191L41.576 23.3613L38.7397 20.5144C37.2953 19.07 34.6786 19.0596 33.1086 20.6191L30.3244 23.4032L27.4357 20.5144C25.9913 19.07 23.3746 19.0596 21.8152 20.6191L19.8788 22.5449L19.816 22.4822L18.2564 20.9226C16.812 19.4782 14.1954 19.4678 12.6254 21.0273L6 27.6736L8.87832 30.5519C9.42258 31.0857 10.1134 31.4416 10.867 31.5672L7.36066 35.0631L11.5892 39.2497C13.0336 40.694 15.6502 40.7046 17.2202 39.145L19.952 36.4028L22.799 39.2601C24.2434 40.7046 26.8705 40.7151 28.43 39.1555L31.0258 36.5494L33.7365 39.2601C35.181 40.7046 37.808 40.7151 39.3676 39.1555L54 24.5022L49.9494 20.5144ZM9.2342 28.2911L8.60619 27.6631L13.0022 23.2672C14.6454 21.6239 16.184 21.4773 17.8901 23.1834L18.4972 23.7905L14.1221 28.2074C12.4893 29.8507 10.9507 30.0077 9.2342 28.2911ZM16.8434 36.9156C15.2002 38.5589 13.6616 38.7054 11.9555 36.9994L9.99826 35.0316L22.3907 22.6601C23.8351 21.2157 25.3738 21.0587 27.0902 22.7752L29.0266 24.7115L23.0711 30.6775L16.8434 36.9156ZM28.0532 36.9156C26.4099 38.5589 24.8713 38.7054 23.1652 36.9994L21.2708 35.0944L27.2682 29.0761L33.4958 22.8589C35.1391 21.2157 36.6776 21.0691 38.3838 22.7752L40.2678 24.6697L34.2599 30.6671L28.0532 36.9156ZM38.9804 36.9156C37.3371 38.5589 35.7985 38.7054 34.0924 36.9994L32.3341 35.2409L38.4675 29.0866L44.6952 22.8694C46.3384 21.2261 47.877 21.0796 49.583 22.7857L51.3624 24.5231L38.9804 36.9156Z" fill="white"/>
                <path d="M24.5887 25.4547C25.2899 25.4547 25.8552 24.8895 25.8656 24.1987C25.8656 23.4974 25.3005 22.9322 24.6096 22.9218C23.9084 22.9218 23.3432 23.487 23.3326 24.1778C23.3326 24.879 23.8979 25.4547 24.5887 25.4547Z" fill="white"/>
                <path d="M35.8927 25.4547C36.5939 25.4337 37.1382 24.8476 37.1277 24.1463C37.1068 23.4765 36.573 22.9322 35.8927 22.9113C35.1913 22.8904 34.6053 23.4451 34.5843 24.1463C34.5634 24.8476 35.1181 25.4337 35.8194 25.4547C35.8403 25.4547 35.8717 25.4547 35.8927 25.4547Z" fill="white"/>
                <path d="M47.1026 22.9217C46.4012 22.9008 45.8152 23.4555 45.7941 24.1568C45.7732 24.8581 46.3279 25.4442 47.0293 25.4651C47.0502 25.4651 47.0815 25.4651 47.1026 25.4651C47.8038 25.486 48.3899 24.9313 48.4108 24.2301C48.4317 23.5288 47.8771 22.9427 47.1758 22.9217C47.1443 22.9217 47.1235 22.9217 47.1026 22.9217Z" fill="white"/>
              </g>
              <defs>
                <clipPath id="clip0_2824_47">
                  <rect width="60" height="60" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </i>
          Login with SSO
        </a>
      </div>
    </>
  )
}

export default LoginForm
